import React from "react";

import PropInput from "components/PropInput";

/**
 * The three customer-logo placement settings, shared by the two Org Management surfaces
 * (OrganizationPage and EditOrganizationModal) because they had drifted-by-copy-paste risk: the blocks
 * were byte-identical, and this repo has been bitten by that before (the five round selectors, now all
 * going through isFoundationLikeRound).
 *
 * All three live inside the org's `logoStyle` object, persisted as the single jsonb column
 * organization.logo_style. That object is materialised here rather than at each call site: the server
 * omits logo_style entirely when unset (@JsonInclude(NON_EMPTY)), and this renders from data straight off
 * both the GET and the save response. Passing PropInput a missing `item` does not throw — it quietly
 * substitutes a throwaway `{}` and writes the edit into that, so the setting would appear to take and
 * then not save. One guard here beats four call sites that can each be forgotten.
 *
 * @param {Object} props
 * @param {!Object} props.org the local (editable) org object (its `logoStyle` is created if absent)
 * @param {!function} props.onChange called after any of the three change
 */
export default function LogoStyleFields({ org, onChange }) {
  // Mutating a prop mid-render, which is how this whole page works: PropInput writes into item[prop]
  // directly and the page re-renders via useRerender(). The object has to be the org's own for the save
  // to pick the values up.
  if (org && !org.logoStyle) org.logoStyle = {};
  const logoStyle = org?.logoStyle;
  const inTopBar = logoStyle?.logoLocation === "top";

  return (
    <>
      <PropInput
        label="Organization Logo Location"
        type="select"
        help="Where the customer logo appears: the left navigation sidebar (default) or the top navigation bar next to the Antaia logo."
        prop="logoLocation"
        item={logoStyle}
        canBeUnset
        options={[
          { label: "Left navigation (default)", value: "left" },
          { label: "Top navigation", value: "top" },
        ]}
        onChange={onChange}
      />

      {!inTopBar && (
        <PropInput
          label="Organization Logo Alignment"
          type="select"
          help="Only applies when the logo is in the left navigation: centered, or aligned to the left edge of the nav icons."
          prop="logoAlignment"
          item={logoStyle}
          canBeUnset
          options={[
            { label: "Center (default)", value: "center" },
            { label: "Left", value: "left" },
          ]}
          onChange={onChange}
        />
      )}

      {inTopBar && (
        <PropInput
          label="Hide Antaia Logo"
          type="checkbox"
          help="Only applies when the logo is in the top navigation, and only takes effect once a logo URL is set — with no customer logo to put there, the Antaia logo stays in the top bar rather than leaving it empty. Checked: the Antaia logo is removed from the top bar (the customer logo takes that spot alone), and a small white Antaia logo with 'Powered by Antaia' appears at the bottom of the left navigation instead. Unchecked: the Antaia logo stays in the top bar alongside the customer logo, as today."
          prop="showAntaiaBranding"
          item={logoStyle}
          onChange={onChange}
        />
      )}
    </>
  );
}
